import {
    useCallback,
    useEffect,
    useState
} from "react";
import {
    useWindowDimensions,
    ActivityIndicator,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitSnackBar,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    TextInput,
    Dialog,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useNavigation
} from "@react-navigation/native";
import type RootStackParamList from "../../../../navigation/type";
import type {
    SystemLocaleItemType
} from "../../../../services/restAPI/actions/getAllSystemLocales/type";
import {
    ArrowLeftIcon,
    SearchIcon,
    PencilIcon,
    Trash2Icon,
    PlusIcon,
    LockIcon
} from "lucide-react-native";
import {
    type NativeStackNavigationProp
} from "@react-navigation/native-stack";
import RESTServiceContext from "../../../../services/restAPI/context";
import syncSystemLocales from "../../../../utils/syncLocales";
import Pagination from "../../../../components/pagination";

const SettingsSystemLocales = () => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        locales,
        setLocales
    ] = useState<Array<SystemLocaleItemType>>([]);

    const [
        isLoading,
        setIsLoading
    ] = useState<boolean>(true);

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState<boolean>(false);

    const [
        isCreateDialogVisible,
        setIsCreateDialogVisible
    ] = useState<boolean>(false);

    const [
        newLocaleKey,
        setNewLocaleKey
    ] = useState<string>("");

    const [
        newLocaleTitleTR,
        setNewLocaleTitleTR
    ] = useState<string>("");

    const [
        newLocaleTitleEN,
        setNewLocaleTitleEN
    ] = useState<string>("");

    const [
        isEditDialogVisible,
        setIsEditDialogVisible
    ] = useState<boolean>(false);

    const [
        editingLocaleKey,
        setEditingLocaleKey
    ] = useState<string>("");

    const [
        editingLocaleTitleTR,
        setEditingLocaleTitleTR
    ] = useState<string>("");

    const [
        editingLocaleTitleEN,
        setEditingLocaleTitleEN
    ] = useState<string>("");

    const [
        isDeleteDialogVisible,
        setIsDeleteDialogVisible
    ] = useState<boolean>(false);

    const [
        selectedDeleteKey,
        setSelectedDeleteKey
    ] = useState<string | null>(null);

    const [
        searchQuery,
        setSearchQuery
    ] = useState<string>("");

    const [
        page,
        setPage
    ] = useState<number>(1);

    const [
        totalCount,
        setTotalCount
    ] = useState<number>(0);

    const fetchLocales = useCallback(async (currentPage: number = 1) => {
        try {
            setIsLoading(true);
            const requestParams: Record<string, string | number> = {
                page: currentPage,
                limit: 20
            };

            if (searchQuery.trim()) {
                requestParams.search = searchQuery.trim();
            }

            const response = await RESTServiceContext.actions.getAllSystemLocales(requestParams);

            if (response && response.code === 200 && Array.isArray(response.payload)) {
                setLocales(response.payload);
                setTotalCount(response.totalCount || 0);
            } else {
                setLocales([]);
                setTotalCount(0);
            }
        } catch {
            setLocales([]);
            setTotalCount(0);
        } finally {
            setIsLoading(false);
        }
    }, [
        searchQuery
    ]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            fetchLocales(1);
            setPage(1);
        }, 200);

        return () => clearTimeout(timeout);
    }, [
        searchQuery,
        fetchLocales
    ]);

    const closeCreateDialog = () => {
        setIsCreateDialogVisible(false);
        setNewLocaleTitleTR("");
        setNewLocaleTitleEN("");
        setNewLocaleKey("");
    };

    const closeEditDialog = () => {
        setIsEditDialogVisible(false);
        setEditingLocaleTitleTR("");
        setEditingLocaleTitleEN("");
        setEditingLocaleKey("");
    };

    const closeDeleteDialog = () => {
        setIsDeleteDialogVisible(false);
        setSelectedDeleteKey(null);
    };

    const handleCreate = async () => {
        const cleanKey = newLocaleKey
            .trim()
            .toLocaleLowerCase("en-US")
            .replace(/[^a-z0-9_-]/g, "-")
            .replace(/-+/g, "-")
            .replace(/^-+|-+$/g, "");
        const cleanTR = newLocaleTitleTR.trim();
        const cleanEN = newLocaleTitleEN.trim();

        if (!cleanKey || !cleanTR) {
            NCoreUIKitSnackBar.open({
                title: localize("system-locales-empty-fields-warning")
            });
            return;
        }

        const isDuplicate = locales.some(l => l.key.toLowerCase() === cleanKey);

        if (isDuplicate) {
            NCoreUIKitSnackBar.open({
                title: localize("key-already-exists")
            });
            return;
        }

        try {
            setIsSubmitting(true);
            const response = await RESTServiceContext.actions.createSystemLocale({
                titleEN: cleanEN || cleanTR,
                titleTR: cleanTR,
                key: cleanKey
            });

            if (response && (response.code === 200 || response.code === 201)) {
                NCoreUIKitSnackBar.open({
                    title: localize("success-create")
                });
                closeCreateDialog();
                fetchLocales(page);
                syncSystemLocales();
            } else if (response && response.message === "key-already-exists") {
                NCoreUIKitSnackBar.open({
                    title: localize("key-already-exists")
                });
            } else {
                NCoreUIKitSnackBar.open({
                    title: response?.message || localize("error-create")
                });
            }
        } catch (error) {
            const err = error as { message?: string };
            if (err?.message === "key-already-exists") {
                NCoreUIKitSnackBar.open({
                    title: localize("key-already-exists")
                });
                return;
            }

            NCoreUIKitSnackBar.open({
                title: localize("error-create")
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEdit = async () => {
        const cleanKey = editingLocaleKey.trim();
        const cleanTR = editingLocaleTitleTR.trim();
        const cleanEN = editingLocaleTitleEN.trim();

        if (!cleanKey || !cleanTR) {
            NCoreUIKitSnackBar.open({
                title: localize("system-locales-empty-fields-warning")
            });
            return;
        }

        try {
            setIsSubmitting(true);
            const response = await RESTServiceContext.actions.updateSystemLocale({
                titleEN: cleanEN || cleanTR,
                titleTR: cleanTR,
                key: cleanKey
            });

            if (response && response.code === 200) {
                NCoreUIKitSnackBar.open({
                    title: localize("success-edit")
                });
                closeEditDialog();
                fetchLocales(page);
                syncSystemLocales();
            } else {
                NCoreUIKitSnackBar.open({
                    title: response?.message || localize("error-edit")
                });
            }
        } catch {
            NCoreUIKitSnackBar.open({
                title: localize("error-edit")
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedDeleteKey) {
            return;
        }

        const targetKey = selectedDeleteKey;
        const targetPage = (locales.length === 1 && page > 1) ? page - 1 : page;

        try {
            setIsSubmitting(true);
            const response = await RESTServiceContext.actions.deleteSystemLocale({
                key: targetKey
            });

            if (response && response.code === 200) {
                NCoreUIKitSnackBar.open({
                    title: localize("success-delete")
                });
                closeDeleteDialog();
                setPage(targetPage);
                fetchLocales(targetPage);
                syncSystemLocales();
            } else {
                NCoreUIKitSnackBar.open({
                    title: response?.message || localize("error-delete")
                });
            }
        } catch {
            NCoreUIKitSnackBar.open({
                title: localize("error-delete")
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderHeader = () => {
        return <View
            style={stylesheet.headerContainer}
        >
            <View
                style={[
                    stylesheet.headerTitleContainer,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Button
                    icon={() => <ArrowLeftIcon
                        color={colors.project.brandWhite}
                        size={24}
                    />}
                    customBorderColor="secondaryButton"
                    onPress={() => navigation.goBack()}
                    customColor="secondaryButton"
                    spreadBehaviour="free"
                    variant="filled"
                />
                <Text
                    variant="titleLargeSize"
                    style={{
                        flexShrink: 1
                    }}
                >
                    {localize("menu-settings-system-locales")}
                </Text>
            </View>
            {!isMobile && <Button
                onPress={() => setIsCreateDialogVisible(true)}
                title={localize("system-locales-create-new")}
                icon={({
                    color,
                    size
                }) => <PlusIcon
                    color={colors.content.icon[color]}
                    size={size}
                />}
                spreadBehaviour="free"
                variant="filled"
            />}
        </View>;
    };

    const renderFilters = () => {
        return <View
            style={[
                stylesheet.filtersContainer,
                {
                    marginTop: spaces.spacingLg,
                    gap: spaces.spacingMd
                }
            ]}
        >
            {isMobile && <Button
                onPress={() => setIsCreateDialogVisible(true)}
                title={localize("system-locales-create-new")}
                icon={({
                    color,
                    size
                }) => <PlusIcon
                    color={colors.content.icon[color]}
                    size={size}
                />}
                spreadBehaviour="stretch"
                variant="filled"
            />}
            <TextInput
                placeholder={localize("system-locales-search-placeholder")}
                customColor={colors.project.brandWhite}
                icon={({
                    color,
                    size
                }) => <SearchIcon
                    color={colors.content.icon[color]}
                    size={size}
                />}
                onChangeText={setSearchQuery}
                spreadBehaviour="stretch"
                isCleanEnabled={true}
                value={searchQuery}
            />
        </View>;
    };

    const renderList = () => {
        if (isLoading && locales.length === 0) {
            return <View
                style={stylesheet.emptyContainer}
            >
                <ActivityIndicator
                    color={colors.project.gold}
                    size="large"
                />
            </View>;
        }

        if (locales.length === 0) {
            return <View
                style={stylesheet.emptyContainer}
            >
                <Text
                    customColor={colors.content.text.mid}
                    variant="bodyMediumSize"
                >
                    {localize("system-locales-no-data-found")}
                </Text>
            </View>;
        }

        return <View
            style={stylesheet.listContainer}
        >
            <View
                style={[
                    stylesheet.listContainer,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                {locales.map((item) => {
                    return <View
                        style={[
                            stylesheet.localeItemWrapper,
                            {
                                backgroundColor: colors.project.brandWhite,
                                padding: spaces.spacingMd,
                                borderRadius: radiuses.md
                            }
                        ]}
                        key={item._id || item.key}
                    >
                        <View
                            style={stylesheet.localeItem}
                        >
                            <View
                                style={[
                                    stylesheet.localeInfoContainer,
                                    {
                                        gap: spaces.spacingSm
                                    }
                                ]}
                            >
                                <View
                                    style={[
                                        stylesheet.localeKeyContainer,
                                        {
                                            gap: spaces.spacingSm
                                        }
                                    ]}
                                >
                                    <Text
                                        variant="titleSmallSize"
                                    >
                                        {item.key}
                                    </Text>
                                    {Boolean(item.isSystemMessage) && <View
                                        style={[
                                            stylesheet.systemBadgeContainer,
                                            {
                                                backgroundColor: colors.content.container.subtle,
                                                paddingHorizontal: spaces.spacingSm,
                                                paddingVertical: spaces.spacingXs,
                                                borderRadius: radiuses.sm,
                                                gap: spaces.spacingXs
                                            }
                                        ]}
                                    >
                                        <LockIcon
                                            color={colors.content.icon.mid}
                                            size={12}
                                        />
                                    </View>}
                                </View>
                                <View
                                    style={[
                                        stylesheet.localeTranslationsContainer,
                                        {
                                            gap: spaces.spacingXs
                                        }
                                    ]}
                                >
                                    <View
                                        style={[
                                            stylesheet.translationRow,
                                            {
                                                gap: spaces.spacingSm
                                            }
                                        ]}
                                    >
                                        <Text
                                            customColor={colors.content.text.mid}
                                            variant="bodySmallSize"
                                        >
                                            {"TR:"}
                                        </Text>
                                        <Text
                                            variant="bodyMediumSize"
                                            style={{
                                                flexShrink: 1
                                            }}
                                        >
                                            {item.titleTR}
                                        </Text>
                                    </View>
                                    <View
                                        style={[
                                            stylesheet.translationRow,
                                            {
                                                gap: spaces.spacingSm
                                            }
                                        ]}
                                    >
                                        <Text
                                            customColor={colors.content.text.mid}
                                            variant="bodySmallSize"
                                        >
                                            {"EN:"}
                                        </Text>
                                        <Text
                                            variant="bodyMediumSize"
                                            style={{
                                                flexShrink: 1
                                            }}
                                        >
                                            {item.titleEN || "-"}
                                        </Text>
                                    </View>
                                </View>
                            </View>
                            <View
                                style={[
                                    stylesheet.actionsContainer,
                                    {
                                        gap: spaces.spacingSm
                                    }
                                ]}
                            >
                                <Button
                                    onPress={() => {
                                        setEditingLocaleTitleTR(item.titleTR || "");
                                        setEditingLocaleTitleEN(item.titleEN || "");
                                        setEditingLocaleKey(item.key);
                                        setIsEditDialogVisible(true);
                                    }}
                                    icon={({
                                        size
                                    }) => <PencilIcon
                                        color={colors.project.brandWhite}
                                        size={size}
                                    />}
                                    customBorderColor="secondaryButton"
                                    customColor="secondaryButton"
                                    spreadBehaviour="free"
                                    variant="filled"
                                />
                                {!item.isSystemMessage && <Button
                                    icon={({
                                        color,
                                        size
                                    }) => <Trash2Icon
                                        color={colors.content.icon[color]}
                                        size={size}
                                    />}
                                    onPress={() => {
                                        setSelectedDeleteKey(item.key);
                                        setIsDeleteDialogVisible(true);
                                    }}
                                    spreadBehaviour="free"
                                    type="danger"
                                />}
                            </View>
                        </View>
                    </View>;
                })}
            </View>
            {totalCount > 0 && <View
                style={[
                    stylesheet.paginationContainer,
                    {
                        marginTop: spaces.spacingLg
                    }
                ]}
            >
                <Pagination
                    onSelect={(item) => {
                        setPage(item.pageNumber);
                        fetchLocales(item.pageNumber);
                    }}
                    totalDataCount={totalCount}
                    currentPage={page}
                    itemPerPage={20}
                />
            </View>}
        </View>;
    };

    const renderCreateDialog = () => {
        return <Dialog
            contentContainerStyle={stylesheet.dialogContentContainer}
            secondaryButtonProps={{
                customTextColor: colors.project.brandWhite,
                customBorderColor: "secondaryButton",
                customColor: "secondaryButton",
                onPress: closeCreateDialog,
                title: localize("cancel"),
                variant: "filled"
            }}
            title={localize("system-locales-create-new")}
            onOverlayPress={closeCreateDialog}
            isVisible={isCreateDialogVisible}
            primaryButtonProps={{
                isLoading: isSubmitting,
                title: localize("save"),
                onPress: handleCreate
            }}
            onClosed={closeCreateDialog}
            variant="yes-no"
        >
            <View
                style={[
                    stylesheet.dialogForm,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <TextInput
                    placeholder={localize("system-locales-key-placeholder")}
                    title={localize("system-locales-key-label")}
                    customColor={colors.project.brandWhite}
                    onChangeText={setNewLocaleKey}
                    spreadBehaviour="stretch"
                    value={newLocaleKey}
                />
                <TextInput
                    placeholder={localize("system-locales-tr-placeholder")}
                    title={localize("system-locales-tr-label")}
                    customColor={colors.project.brandWhite}
                    onChangeText={setNewLocaleTitleTR}
                    spreadBehaviour="stretch"
                    value={newLocaleTitleTR}
                />
                <TextInput
                    placeholder={localize("system-locales-en-placeholder")}
                    title={localize("system-locales-en-label")}
                    customColor={colors.project.brandWhite}
                    onChangeText={setNewLocaleTitleEN}
                    spreadBehaviour="stretch"
                    value={newLocaleTitleEN}
                />
            </View>
        </Dialog>;
    };

    const renderEditDialog = () => {
        return <Dialog
            contentContainerStyle={stylesheet.dialogContentContainer}
            secondaryButtonProps={{
                customTextColor: colors.project.brandWhite,
                customBorderColor: "secondaryButton",
                customColor: "secondaryButton",
                title: localize("cancel"),
                onPress: closeEditDialog,
                variant: "filled"
            }}
            title={localize("system-locales-edit-title")}
            onOverlayPress={closeEditDialog}
            isVisible={isEditDialogVisible}
            primaryButtonProps={{
                isLoading: isSubmitting,
                title: localize("save"),
                onPress: handleEdit
            }}
            onClosed={closeEditDialog}
            variant="yes-no"
        >
            <View
                style={[
                    stylesheet.dialogForm,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <TextInput
                    title={localize("system-locales-key-label")}
                    customColor={colors.project.brandWhite}
                    spreadBehaviour="stretch"
                    value={editingLocaleKey}
                    isDisabled={true}
                />
                <TextInput
                    placeholder={localize("system-locales-tr-placeholder")}
                    title={localize("system-locales-tr-label")}
                    customColor={colors.project.brandWhite}
                    onChangeText={setEditingLocaleTitleTR}
                    value={editingLocaleTitleTR}
                    spreadBehaviour="stretch"
                />
                <TextInput
                    placeholder={localize("system-locales-en-placeholder")}
                    title={localize("system-locales-en-label")}
                    customColor={colors.project.brandWhite}
                    onChangeText={setEditingLocaleTitleEN}
                    value={editingLocaleTitleEN}
                    spreadBehaviour="stretch"
                />
            </View>
        </Dialog>;
    };

    const renderDeleteDialog = () => {
        return <Dialog
            primaryButtonProps={{
                icon: ({
                    color,
                    size
                }) => <Trash2Icon
                    color={colors.content.icon[color as never] || color}
                    size={size}
                />,
                title: localize("delete"),
                isLoading: isSubmitting,
                onPress: handleDelete,
                type: "danger"
            }}
            title={localize("system-locales-delete-confirm-title")}
            secondaryButtonProps={{
                customTextColor: colors.project.brandWhite,
                customBorderColor: "secondaryButton",
                customColor: "secondaryButton",
                onPress: closeDeleteDialog,
                title: localize("cancel"),
                variant: "filled"
            }}
            onOverlayPress={closeDeleteDialog}
            isVisible={isDeleteDialogVisible}
            onClosed={closeDeleteDialog}
            variant="yes-no"
        >
            <View
                style={{
                    gap: spaces.spacingSm
                }}
            >
                {Boolean(selectedDeleteKey) && <View
                    style={[
                        stylesheet.deleteKeyBadge,
                        {
                            backgroundColor: colors.content.container.subtle,
                            paddingHorizontal: spaces.spacingSm,
                            paddingVertical: spaces.spacingXs,
                            borderRadius: radiuses.sm
                        }
                    ]}
                >
                    <Text
                        variant="titleSmallSize"
                    >
                        {selectedDeleteKey}
                    </Text>
                </View>}
                <Text
                    customColor={colors.content.text.mid}
                    variant="bodyMediumSize"
                >
                    {localize("system-locales-delete-confirm-desc")}
                </Text>
            </View>
        </Dialog>;
    };

    return <PageContainer
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: spaces.spacingXl,
                    gap: spaces.spacingXl
                }
            ]}
        >
            {renderHeader()}
            {renderFilters()}
            {renderList()}
        </View>
        {renderCreateDialog()}
        {renderDeleteDialog()}
        {renderEditDialog()}
    </PageContainer>;
};
export default SettingsSystemLocales;
